import { useCallback } from 'react';
import {
  useAddDescriptor,
  useDeleteDescriptor,
  useUpdateDescriptor,
  type EntityDescriptorDto,
} from '../api/queries/entities';

/**
 * Encapsulates find-or-create logic for single-value descriptor categories.
 * - getValue(category): returns the current content or ''
 * - upsert(category, content): creates, updates, or deletes the descriptor
 */
export function useDescriptorUpsert(
  entityId: string,
  descriptors: EntityDescriptorDto[],
) {
  const add = useAddDescriptor(entityId);
  const update = useUpdateDescriptor(entityId);
  const remove = useDeleteDescriptor(entityId);

  const getValue = useCallback(
    (category: string): string => {
      return (
        descriptors.find(
          (d) => d.category.toLowerCase() === category.toLowerCase(),
        )?.content ?? ''
      );
    },
    [descriptors],
  );

  const getDescriptor = useCallback(
    (category: string): EntityDescriptorDto | undefined => {
      return descriptors.find(
        (d) => d.category.toLowerCase() === category.toLowerCase(),
      );
    },
    [descriptors],
  );

  const upsert = useCallback(
    async (category: string, content: string) => {
      const existing = getDescriptor(category);

      if (!content.trim()) {
        if (existing) {
          await remove.mutateAsync(existing.id);
        }
        return;
      }

      if (existing) {
        if (existing.content === content.trim()) return; // no change
        await update.mutateAsync({
          id: existing.id,
          category: existing.category,
          content: content.trim(),
          sequenceOrder: existing.sequenceOrder,
        });
      } else {
        await add.mutateAsync({ category, content: content.trim() });
      }
    },
    [getDescriptor, add, update, remove],
  );

  const isPending = add.isPending || update.isPending || remove.isPending;

  return { getValue, getDescriptor, upsert, isPending };
}
